import type { FetchArgs } from '@reduxjs/toolkit/query';

export type CapsuleStatus = 'active' | 'pending' | 'inactive';

export enum ClusterType {
  FULL = 'full',
  CAPSULE = 'capsule',
}

export interface CapsuleDto {
  id: number | string;
  name: string;
  cluster_type: ClusterType | string;
  uid: string;
  config: Record<string, unknown> | null;
  capabilities: Record<string, unknown> | null;
  org_id?: number;
  is_default?: boolean;
  is_private?: boolean;
  description?: string | null;
  status?: string;
  provider?: string;
  updated_at?: string;
  created_at?: string;
  last_heartbeat_at?: string | null;
  registered_at?: string | null;
  registered_by?: string | null;
  api_access_key?: string;
  registration_key?: string;
}

export type CapsuleResponseWithKeys = CapsuleDto;

export interface AllowedClustersParams {
  org_id: number | string;
}

export type ClusterKind = ClusterType;

export interface CreateClusterPayload {
  org_id: number;
  name: string;
  description: string;
  cluster_type: ClusterType;
  region?: string;
}

export const CLUSTER_BASE_PATH = '/clusters';

export interface UpdateCapsulePayload {
  name?: string;
  status?: CapsuleStatus;
  config?: Record<string, unknown> | null;
}

export interface UpdateCapsuleArg {
  id: string;
  body: UpdateCapsulePayload;
}

export function getAllowedClustersByOrgQueryDefinition(
  params: AllowedClustersParams,
): string | FetchArgs {
  const orgId = encodeURIComponent(String(params.org_id));
  return { url: `/orgs/${orgId}/allowed_clusters` };
}

export function getCapsuleQueryDefinition(id: string): string {
  return `${CLUSTER_BASE_PATH}/${encodeURIComponent(id)}`;
}

export function createClusterMutationDefinition(body: CreateClusterPayload): FetchArgs {
  return {
    url: CLUSTER_BASE_PATH,
    method: 'POST',
    body,
  };
}

export function updateCapsuleMutationDefinition(arg: UpdateCapsuleArg): FetchArgs {
  return {
    url: `${CLUSTER_BASE_PATH}/${encodeURIComponent(arg.id)}`,
    method: 'PATCH',
    body: arg.body,
  };
}

export function deleteCapsuleMutationDefinition(id: string): FetchArgs {
  return {
    url: `${CLUSTER_BASE_PATH}/${encodeURIComponent(id)}`,
    method: 'DELETE',
  };
}

export function parseCapsulesListResponse(raw: unknown): CapsuleDto[] {
  if (Array.isArray(raw)) return raw as CapsuleDto[];

  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.items)) {
      return obj.items as CapsuleDto[];
    }

    if (Array.isArray(obj.allowed_clusters)) {
      return obj.allowed_clusters as CapsuleDto[];
    }
  }
  return [];
}
